/*
 *   File : faq.js
 *   Author : https://evoqins.com
 *   Description : faq component
 *   Integrations : NA
 *   Version : 1.0.0
 */
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setPageTitle, setSearchEnabled } from "../../../../Redux/Ui/ui.slice";
import { Accordion2 } from "../../../../Components/Accordion";
import { useNavigate } from "react-router-dom";
import { APIService } from "../../../../Service";
import { EmptyScreen, Loader } from "../../../../Components/Other";
import { ConfirmationModal } from "../../../../Components/Modals";
import { toast } from "react-toastify";

export default function Faq() {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(null);
  const [faqData, setFaqData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [question, setQuestion] = useState({});

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setPageTitle(["Manage Content"]));
    dispatch(setSearchEnabled(false));

    _getFaqList();
  }, []);

  const _getFaqList = async () => {
    try {
      setLoader(true);

      const res = await APIService(false, "content/list-faq");

      if (res?.status_code === 200) {
        const faqs = res?.data || [];

        setFaqData(faqs);
        setIsEmpty(faqs.length === 0);
      } else {
        setFaqData([]);
        setIsEmpty(true);
      }
    } catch (err) {
      console.error("FAQ list error:", err);
      setFaqData([]);
      setIsEmpty(true);
    } finally {
      setLoader(false);
    }
  };

  const handleToggle = (index) => {
    setOpenIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  // Split FAQ data into two columns
  const leftColumnItems = faqData.filter((_, index) => index % 2 === 0);
  const rightColumnItems = faqData.filter((_, index) => index % 2 === 1);

  const _deleteFaq = () => {
    setLoader(true);

    const payload = {
      question_id: question?.question_id,
    };

    APIService(true, "content/delete-faq", payload)
      .then((res) => {
        if (res?.status_code === 200) {
          toast.success(res?.message);
          _getFaqList();
        } else {
          toast.error(res?.message || "Failed to delete FAQ");
        }
      })
      .catch((error) => {
        console.error("Error deleting FAQ:", error);
        toast.error(error?.message || "Something went wrong");
      })
      .finally(() => {
        setLoader(false);
        setDeleteModal(false);
      });
  };

  if (loader) {
    return <Loader />;
  }

  if (isEmpty) {
    return <EmptyScreen message="No Faq's Found" />;
  }

  return (
    <div className="d-flex w-100 px-24 pb-24">
      <div className="row w-100">
        {/* Left Column */}
        <div className="col-md-6 col-12 d-flex flex-column rg-12">
          {leftColumnItems.map((item, index) => {
            const originalIndex = index * 2;

            return (
              <Accordion2
                key={item.question_id}
                title={item.question}
                content={item.answer}
                isOpen={openIndex === originalIndex}
                onClick={() => handleToggle(originalIndex)}
                handleDelete={() => {
                  setQuestion(item);
                  setDeleteModal(true);
                }}
                handleEdit={() =>
                  navigate(`/manage-content/faq/edit/${item.question_id}`)
                }
              />
            );
          })}
        </div>
        {/* Right Column */}
        <div className="col-md-6 col-12 d-flex flex-column rg-12">
          {rightColumnItems.map((item, index) => {
            const originalIndex = index * 2 + 1;

            return (
              <Accordion2
                key={item.question_id}
                title={item.question}
                content={item.answer}
                isOpen={openIndex === originalIndex}
                onClick={() => handleToggle(originalIndex)}
                handleDelete={() => {
                  setQuestion(item);
                  setDeleteModal(true);
                }}
                handleEdit={() =>
                  navigate(`/manage-content/faq/edit/${item.question_id}`)
                }
              />
            );
          })}
        </div>
      </div>
      <ConfirmationModal
        open={deleteModal}
        title="Delete FAQ"
        message="Are you sure you want to delete this FAQ?"
        confirmText={loader ? "Deleting..." : "Yes, Delete"}
        cancelText="Cancel"
        onCancel={() => {
          if (!loader) setDeleteModal(false);
        }}
        onConfirm={_deleteFaq}
      />
    </div>
  );
}
